import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Trophy } from "lucide-react";

export default function StudentTestAttempt() {
  const { id } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tests/${id}`);
        setTest(data);
        setAnswers(new Array(data.questions.length).fill(-1));
        setSecondsLeft(data.duration_minutes * 60);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        nav(-1);
      }
    })();
  }, [id, nav]);

  useEffect(() => {
    if (!secondsLeft || result) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft, result]);

  useEffect(() => {
    if (test && secondsLeft === 0 && !result) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/student/tests/${id}/submit`, { answers });
      setResult(data);
      toast.success(`You scored ${data.percent}%`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!test) return <div className="p-12 text-center text-blue-600 font-heading animate-pulse">Loading test…</div>;

  if (result) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-blue-600 text-white grid place-items-center">
          <Trophy className="h-10 w-10" />
        </div>
        <h1 className="mt-6 font-heading font-black text-4xl text-slate-900">{result.percent}%</h1>
        <p className="mt-2 text-slate-600">{result.score} out of {result.total} correct</p>
        <button onClick={() => nav("/dashboard/student/tests")} data-testid="back-to-tests"
          className="mt-8 px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700">
          Back to tests
        </button>
      </div>
    );
  }

  const q = test.questions[current];
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const answered = answers.filter((a) => a >= 0).length;

  return (
    <div className="max-w-3xl mx-auto" data-testid="test-attempt">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">{test.title}</h1>
          <p className="text-sm text-slate-500">{answered}/{test.questions.length} answered</p>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${secondsLeft < 60 ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-700"} font-mono font-bold`}>
          <Clock className="h-4 w-4" />
          {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="text-xs font-bold uppercase tracking-wider text-blue-600">
          Question {current + 1} of {test.questions.length}
        </div>
        <h2 className="mt-2 font-heading font-bold text-xl text-slate-900">{q.text}</h2>

        <div className="mt-5 space-y-2">
          {q.options.map((opt, j) => (
            <button key={j}
              data-testid={`option-${current}-${j}`}
              onClick={() => setAnswers((a) => { const c = [...a]; c[current] = j; return c; })}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                answers[current] === j
                  ? "border-blue-500 bg-blue-50 text-blue-900"
                  : "border-slate-200 hover:border-blue-300 text-slate-700"
              }`}>
              <span className="font-mono font-bold mr-3">{String.fromCharCode(65 + j)}.</span>
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {test.questions.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)}
            className={`w-8 h-8 rounded-lg text-xs font-bold ${
              i === current ? "bg-blue-600 text-white" :
              answers[i] >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
            }`}>
            {i + 1}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        {current < test.questions.length - 1 ? (
          <button onClick={() => setCurrent((c) => c + 1)} data-testid="next-question"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="submit-test"
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
            {submitting ? "Submitting..." : "Submit test"}
          </button>
        )}
      </div>
    </div>
  );
}
